import { drag } from 'd3-drag';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import { select } from 'd3-selection';
import 'd3-transition';
import { zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  CATEGORY_LABELS,
  glyphOf,
  type Category,
  type GraphLink,
  type GraphNode,
} from '@/papermill/extraction';

import styles from './ExtractionGraph.module.css';

export interface ExtractionGraphProps {
  nodes: GraphNode[];
  links: GraphLink[];
  onSelect: (id: string) => void;
  /** The canvas height; by default 600, or 290 on a narrow screen. */
  height?: number;
  /** Take the height of the parent, a flex column, in place of `height`. */
  fill?: boolean;
}

type Node = GraphNode & SimulationNodeDatum;
type Link = Omit<GraphLink, 'source' | 'target'> & SimulationLinkDatum<Node>;

const RADIUS = 24;
const CATEGORY_ORDER: Category[] = [
  'person',
  'organization',
  'place',
  'money',
  'work',
  'identity',
  'document',
];

/** An extraction as a force-directed graph: drag nodes, pan and zoom, click to inspect. */
export function ExtractionGraph({
  nodes,
  links,
  onSelect,
  height: requestedHeight,
  fill = false,
}: ExtractionGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const simRef = useRef<Simulation<Node, Link> | null>(null);
  const fitRef = useRef<() => void>(() => {});
  const onSelectRef = useRef(onSelect);
  const [width, setWidth] = useState(800);
  const [fillHeight, setFillHeight] = useState(0);
  const narrow = width < 560;
  const height = fill ? Math.max(200, fillHeight) : (requestedHeight ?? (narrow ? 290 : 600));

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(320, entry.contentRect.width));
      setFillHeight(Math.round(entry.contentRect.height));
    });
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  const present = useMemo(
    () => CATEGORY_ORDER.filter((category) => nodes.some((node) => node.category === category)),
    [nodes],
  );

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) {
      return;
    }
    const svg = select(svgEl);
    svg.selectAll('g.viewport').remove();
    const viewport = svg.append('g').attr('class', 'viewport');

    const simNodes: Node[] = nodes.map((node) => ({ ...node }));
    const simLinks: Link[] = links.map((link) => ({ ...link }));
    const neighbours = new Map<string, Set<string>>();
    for (const link of links) {
      if (!neighbours.has(link.source)) neighbours.set(link.source, new Set());
      if (!neighbours.has(link.target)) neighbours.set(link.target, new Set());
      neighbours.get(link.source)!.add(link.target);
      neighbours.get(link.target)!.add(link.source);
    }

    const linkGroup = viewport.append('g').attr('class', styles.links);
    const linkLines = linkGroup
      .selectAll('line')
      .data(simLinks)
      .join('line')
      .attr('class', styles.link)
      .attr('marker-end', 'url(#arrow)');
    const linkLabels = linkGroup
      .selectAll('text')
      .data(simLinks)
      .join('text')
      .attr('class', styles.linkLabel)
      .text((link) => link.label);

    const nodeGroups = viewport
      .append('g')
      .selectAll<SVGGElement, Node>('g')
      .data(simNodes)
      .join('g')
      .attr('class', (node) => `${styles.node} ${styles[node.category]}`)
      .attr('role', 'button')
      .attr('tabindex', 0)
      .attr('aria-label', (node) => `${node.typeLabel}: ${node.label}`)
      .on('click', (_event, node) => onSelectRef.current(node.id))
      .on('keydown', (event: KeyboardEvent, node) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectRef.current(node.id);
        }
      })
      .on('mouseenter focus', (_event, node) => {
        const near = neighbours.get(node.id) ?? new Set<string>();
        nodeGroups.classed(styles.dim, (other) => other.id !== node.id && !near.has(other.id));
        const touches = (link: Link) =>
          (link.source as Node).id === node.id || (link.target as Node).id === node.id;
        linkLines.classed(styles.dim, (link) => !touches(link)).classed(styles.active, touches);
        linkLabels.classed(styles.dim, (link) => !touches(link));
      })
      .on('mouseleave blur', () => {
        nodeGroups.classed(styles.dim, false);
        linkLines.classed(styles.dim, false).classed(styles.active, false);
        linkLabels.classed(styles.dim, false);
      });

    nodeGroups
      .append('circle')
      .attr('r', (node) => (node.category === 'document' ? RADIUS - 6 : RADIUS));
    nodeGroups
      .append('text')
      .attr('class', styles.glyph)
      .attr('dy', '0.35em')
      .text((node) => glyphOf(node.typeLabel));
    nodeGroups
      .append('text')
      .attr('class', styles.nodeLabel)
      .attr('y', RADIUS + 16)
      .text((node) => node.label);
    nodeGroups
      .append('text')
      .attr('class', styles.nodeType)
      .attr('y', RADIUS + 30)
      .text((node) => node.typeLabel);

    const simulation = forceSimulation<Node>(simNodes)
      .force(
        'link',
        forceLink<Node, Link>(simLinks)
          .id((node) => node.id)
          .distance(170)
          .strength(0.5),
      )
      .force('charge', forceManyBody().strength(-1100))
      .force('center', forceCenter(width / 2, height / 2))
      .force('x', forceX(width / 2).strength(narrow ? 0.05 : 0.015))
      .force('y', forceY(height / 2).strength(width > 700 ? 0.14 : narrow ? 0.08 : 0.02))
      .force('collide', forceCollide(RADIUS + 48))
      .stop();
    simRef.current = simulation;

    const draw = () => {
      linkLines
        .attr('x1', (link) => (link.source as Node).x ?? 0)
        .attr('y1', (link) => (link.source as Node).y ?? 0)
        .attr('x2', (link) => {
          const s = link.source as Node;
          const t = link.target as Node;
          const dx = (t.x ?? 0) - (s.x ?? 0);
          const dy = (t.y ?? 0) - (s.y ?? 0);
          const length = Math.hypot(dx, dy) || 1;
          return (t.x ?? 0) - (dx / length) * (RADIUS + 4);
        })
        .attr('y2', (link) => {
          const s = link.source as Node;
          const t = link.target as Node;
          const dx = (t.x ?? 0) - (s.x ?? 0);
          const dy = (t.y ?? 0) - (s.y ?? 0);
          const length = Math.hypot(dx, dy) || 1;
          return (t.y ?? 0) - (dy / length) * (RADIUS + 4);
        });
      linkLabels
        .attr('x', (link) => (((link.source as Node).x ?? 0) + ((link.target as Node).x ?? 0)) / 2)
        .attr(
          'y',
          (link) => (((link.source as Node).y ?? 0) + ((link.target as Node).y ?? 0)) / 2 - 4,
        );
      nodeGroups.attr('transform', (node) => `translate(${node.x ?? 0},${node.y ?? 0})`);
    };
    simulation.on('tick', draw);

    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.25, 3])
      .on('zoom', (event) => viewport.attr('transform', event.transform.toString()));
    zoomRef.current = zoomBehavior;
    svg.call(zoomBehavior).on('dblclick.zoom', null);

    const fit = (animate = true) => {
      if (simNodes.length === 0) {
        return;
      }
      const xs = simNodes.map((node) => node.x ?? 0);
      const ys = simNodes.map((node) => node.y ?? 0);
      const [minX, maxX] = [Math.min(...xs) - 110, Math.max(...xs) + 110];
      const [minY, maxY] = [Math.min(...ys) - RADIUS - 12, Math.max(...ys) + RADIUS + 40];
      const top = narrow ? 16 : 64;
      const bottom = narrow ? 12 : 36;
      const scale = Math.min(1.3, width / (maxX - minX), (height - top - bottom) / (maxY - minY));
      const transform = zoomIdentity
        .translate(width / 2, top + (height - top - bottom) / 2)
        .scale(scale)
        .translate(-(minX + maxX) / 2, -(minY + maxY) / 2);
      if (animate) {
        svg.transition().duration(600).call(zoomBehavior.transform, transform);
      } else {
        svg.call(zoomBehavior.transform, transform);
      }
    };
    fitRef.current = fit;

    const ticks = simNodes.length > 150 ? 120 : simNodes.length > 60 ? 220 : 400;
    for (let i = 0; i < ticks; i += 1) {
      simulation.tick();
    }
    draw();
    fit(false);

    nodeGroups.call(
      drag<SVGGElement, Node>()
        .on('start', (event, node) => {
          if (!event.active) simulation.alphaTarget(0.2).restart();
          node.fx = node.x;
          node.fy = node.y;
        })
        .on('drag', (event, node) => {
          node.fx = event.x;
          node.fy = event.y;
        })
        .on('end', (event) => {
          if (!event.active) simulation.alphaTarget(0);
        }),
    );

    return () => {
      simulation.stop();
      viewport.remove();
    };
  }, [nodes, links, width, height, narrow]);

  function zoomBy(factor: number) {
    const svgEl = svgRef.current;
    if (svgEl && zoomRef.current) {
      select(svgEl).transition().duration(250).call(zoomRef.current.scaleBy, factor);
    }
  }

  function relayout() {
    const simulation = simRef.current;
    if (!simulation) {
      return;
    }
    for (const node of simulation.nodes()) {
      node.fx = null;
      node.fy = null;
    }
    simulation.alpha(0.9).restart();
    window.setTimeout(() => fitRef.current(), 1400);
  }

  return (
    <div className={[styles.frame, fill ? styles.fill : ''].filter(Boolean).join(' ')}>
      <svg
        ref={svgRef}
        className={styles.canvas}
        height={height}
        role="group"
        aria-label={`Extraction graph: ${nodes.length} entities, ${links.length} relationships`}
      >
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className={styles.arrow} />
          </marker>
        </defs>
      </svg>

      <div className={styles.controls}>
        <button type="button" onClick={() => zoomBy(1.3)} aria-label="Zoom in">
          +
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.3)} aria-label="Zoom out">
          −
        </button>
        <button type="button" onClick={() => fitRef.current()} aria-label="Fit to view">
          ⤢
        </button>
        <button type="button" onClick={relayout} aria-label="Re-arrange">
          ↻
        </button>
      </div>

      {present.length > 1 ? (
        <ul className={styles.legend} aria-label="Legend">
          {present.map((category) => (
            <li key={category}>
              <span className={`${styles.swatch} ${styles[category]}`} aria-hidden="true" />
              {CATEGORY_LABELS[category]}
            </li>
          ))}
        </ul>
      ) : null}

      <p className={styles.hint}>Drag to move · scroll or pinch to zoom · click an entity</p>
    </div>
  );
}
